import {
    API_ENDPOINTS,
    customToast,
    FormInput,
    route,
    SelectOption,
    Stepper,
    StringHelpers,
    useDisabledContext,
    useLangContext,
    useQueries,
    SubTitle,
    Variants,
    ValueType,
} from 'carrier-fe';
import { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { ClaimsFormData, DistributorDetails } from '../../types/claim';
import { ToshibaInstallation } from '../../types/installation';
import { UseFormReturn } from '../../types/journey';
import { DISTRIBUTOR_CLAIM_TYPES, QUERY_KEYS } from '../../util/Constants';
import { fetchProductLines, fetchProductModels } from '../../util/toshiba-utils';

type SelectKey = 'installation_systems' | 'product_children' | 'product_types' | 'product_lines' | 'product_models';
type Selects = Record<SelectKey, SelectOption[]>;

function ClaimsStep2({
    form,
}: {
    form: UseFormReturn<ClaimsFormData, any>;
}) {
    // Hooks and Contexts
    const { setValues, store, errors } = form;
    const { crud, fields } = useLangContext();
    const { disabled, setDisabled } = useDisabledContext();

    // State Management
    const [selects, setSelects] = useState<Selects>({
        installation_systems: [],
        product_children: [],
        product_types: [],
        product_lines: [],
        product_models: [],
    });
    const [distributorDetails, setDistributorDetails] = useState<DistributorDetails>({
        distributor_claim_type: null,
        dispatch_note_field_label: null,
        dispatch_note_required: false,
    });
    const [installationSystemSelected, setInstallationSystemSelected] = useState(!!store?.toshiba_installation_system_id);
    const [installationProductChildId, setInstallationProductChildId] = useState<string>('');
    const prevProductChildId = useRef(store?.product_child_id);
    const prevProductTypeId = useRef(store?.product_type_id);
    const prevProductLineId = useRef(store?.product_line_id);

    // Queries
    const hasInstallation = !!store?.toshiba_installation_id;

    const getQueries = () => {
        return [
            {
                key: QUERY_KEYS.PRODUCT_CHILDREN,
                config: {
                    method: 'GET',
                    url: route(API_ENDPOINTS.GENERIC.SELECT.PRODUCT.CHILDREN),
                },
            },
            ...store?.product_child_id ? [
                {
                    key: QUERY_KEYS.PRODUCT_TYPES,
                    config: {
                        method: 'GET',
                        url: route(
                            API_ENDPOINTS.GENERIC.SELECT.PRODUCT.TYPES, 
                            { product_child_id: store?.product_child_id }
                        ),
                    },
                },
            ] : [],
        ];
    };

    const {
        data: selectOptions,
        error,
        isLoading,
        refetch,
    } = useQueries<{ data: SelectOption[] }>(getQueries());

    // Effects

    // Fetch distributor details on mount to get claim type and dispatch note config
    useEffect(() => {
        if (!store?.distributor_id) return;

        const fetchDistributorDetails = async () => {
            try {
                const response = await axios.get<{ data: DistributorDetails }>(
                    route(
                        API_ENDPOINTS.REGISTRATION.CLAIM.DISTRIBUTOR_CONFIG,
                        { distributorId: store.distributor_id }
                    )
                );

                const distributor = response.data?.data;

                if (distributor) {
                    setDistributorDetails({
                        distributor_claim_type: distributor.distributor_claim_type ?? null,
                        dispatch_note_field_label: distributor.dispatch_note_field_label ?? null,
                        dispatch_note_required: distributor.dispatch_note_required ?? false,
                    });
                }
            } catch (err: any) {
                customToast({
                    title: err?.response?.data?.message || 'Failed to load distributor details.',
                    variant: Variants.Danger,
                });
            }
        };

        fetchDistributorDetails();
    }, [store?.distributor_id]);

    // Fetch installation systems and installation product_child_id when an installation is linked
    useEffect(() => {
        if (!store?.toshiba_installation_id) return;

        const fetchInstallationSystems = async () => {
            setDisabled(true);

            try {
                const response = await axios.get<{ data: SelectOption[] }>(
                    route(API_ENDPOINTS.GENERIC.SELECT.INSTALLATION_SYSTEMS, {
                        installationId: store.toshiba_installation_id,
                    })
                );

                setSelects((prev) => ({
                    ...prev,
                    installation_systems: response.data?.data ?? [],
                }));
            } catch (err: any) {
                customToast({
                    title: err?.response?.data?.message || 'Failed to load installation systems.',
                    variant: Variants.Danger,
                });
            } finally {
                setDisabled(false);
            }
        };

        // Also fetch the installation to get product_child_id (installation-level)
        const fetchInstallationDetails = async () => {
            try {
                const response = await axios.get<{ data: ToshibaInstallation }>(
                    route(API_ENDPOINTS.REGISTRATION.INSTALLATION.VIEW, {
                        installationId: store.toshiba_installation_id,
                    })
                );

                const productChildId = response.data?.data?.product_child_id || '';
                setInstallationProductChildId(productChildId);

                if (productChildId && !store?.product_child_id) {
                    setValues({ product_child_id: productChildId });
                }
            } catch (err: any) {
                // Silently fail - product child can be selected manually
            }
        };

        fetchInstallationSystems();
        fetchInstallationDetails();
    }, [store?.toshiba_installation_id]);

    // Update selects when query data arrives
    useEffect(() => {
        if (!selectOptions) return;

        setSelects((prev) => ({
            ...prev,
            product_children: selectOptions[QUERY_KEYS.PRODUCT_CHILDREN]?.data ?? [],
            product_types: selectOptions[QUERY_KEYS.PRODUCT_TYPES]?.data ?? [],
        }));
    }, [selectOptions]);

    // Handle query errors
    useEffect(() => {
        Object.values(error).forEach((err: any) => {
            if (!err) return;

            customToast({
                title: err?.response?.data?.message || 'An error occurred.',
                variant: Variants.Danger,
            });
        });
    }, [error]);

    // When product_child changes, refetch product types and reset dependent fields
    useEffect(() => {
        if (!store?.product_child_id) return;

        refetch([QUERY_KEYS.PRODUCT_TYPES]);

        // Only reset dependents if the value actually changed (skip on remount with same value)
        if (prevProductChildId.current === store.product_child_id) return;
        prevProductChildId.current = store.product_child_id;

        // Only reset dependents if the change was user-initiated (not from system prefill)
        if (!installationSystemSelected) {
            setValues({
                product_type_id: '',
                product_line_id: '',
                product_id: '',
            });
            setSelects((prev) => ({
                ...prev,
                product_lines: [],
                product_models: [],
            }));
        }
    }, [store?.product_child_id]);

    // When product_type changes, fetch product lines
    useEffect(() => {
        if (!store?.product_type_id || !store?.product_child_id) return;

        const loadProductLines = async () => {
            try {
                const lines = await fetchProductLines({
                    childId: store.product_child_id,
                    typeId: store.product_type_id,
                    setDisabled,
                });

                setSelects((prev) => ({
                    ...prev,
                    product_lines: lines || [],
                }));
            } catch (err: any) {
                customToast({
                    title: err?.response?.data?.message || 'Failed to load product lines.',
                    variant: Variants.Danger,
                });
            }
        };

        loadProductLines();

        if (prevProductTypeId.current === store.product_type_id) return;
        prevProductTypeId.current = store.product_type_id;

        if (!installationSystemSelected) {
            setValues({
                product_line_id: '',
                product_id: '',
            });
            setSelects((prev) => ({
                ...prev,
                product_models: [],
            }));
        }
    }, [store?.product_type_id]);

    // When product_line changes, fetch product models
    useEffect(() => {
        if (!store?.product_line_id || !store?.product_child_id || !store?.product_type_id) return;

        const loadProductModels = async () => {
            try {
                const models = await fetchProductModels({
                    childId: store.product_child_id,
                    typeId: store.product_type_id,
                    lineId: store.product_line_id,
                    setDisabled,
                });

                setSelects((prev) => ({
                    ...prev,
                    product_models: models || [],
                }));
            } catch (err: any) {
                customToast({
                    title: err?.response?.data?.message || 'Failed to load product models.',
                    variant: Variants.Danger,
                });
            }
        };

        loadProductModels();

        if (prevProductLineId.current === store.product_line_id) return;
        prevProductLineId.current = store.product_line_id;

        if (!installationSystemSelected) {
            setValues({ product_id: '' });
        }
    }, [store?.product_line_id]);

    // Derived States
    const isPostRepair = useMemo(
        () => distributorDetails.distributor_claim_type === DISTRIBUTOR_CLAIM_TYPES.POST_REPAIR,
        [distributorDetails.distributor_claim_type]
    );

    const isPreRepair = useMemo(
        () => distributorDetails.distributor_claim_type === DISTRIBUTOR_CLAIM_TYPES.PRE_REPAIR,
        [distributorDetails.distributor_claim_type]
    );

    // Handlers

    const handleInstallationSystemChange = async (value: ValueType | null) => {
        if (!value) {
            // Clear system selection and unlock product fields
            setInstallationSystemSelected(false);
            setValues({
                toshiba_installation_system_id: '',
                product_type_id: '',
                product_line_id: '',
                product_id: '',
                serial_number: '',
            });
            // Restore product_child from installation level if available
            if (installationProductChildId) {
                setValues({ product_child_id: installationProductChildId });
            }
            setSelects((prev) => ({
                ...prev,
                product_lines: [],
                product_models: [],
            }));
            return;
        }

        setDisabled(true);
        setInstallationSystemSelected(true);

        if (typeof value !== 'string') return;

        try {
            // Fetch the full installation to get the selected system's details
            const response = await axios.get<{ data: ToshibaInstallation }>(
                route(
                    API_ENDPOINTS.REGISTRATION.INSTALLATION.VIEW, 
                    { installationId: store?.toshiba_installation_id }
                )
            );

            const installation = response.data?.data;
            const system = installation?.installation_systems?.find((s) => s.id === value);

            if (system) {
                // Set the product_child from installation level
                const productChildId = installation?.product_child_id || '';

                setValues({
                    toshiba_installation_system_id: value,
                    product_child_id: productChildId,
                    product_type_id: system.product_type_id || '',
                    product_line_id: system.product_line_id || '',
                    product_id: system.product_id || '',
                    serial_number: system.serial_number || '',
                });

                // Fetch the dropdown options for each level so values display correctly
                if (productChildId) {
                    refetch([QUERY_KEYS.PRODUCT_TYPES]);
                }

                if (productChildId && system.product_type_id) {
                    const lines = await fetchProductLines({
                        childId: productChildId,
                        typeId: system.product_type_id,
                    });
                    setSelects((prev) => ({
                        ...prev,
                        product_lines: lines || [],
                    }));
                }

                if (productChildId && system.product_type_id && system.product_line_id) {
                    const models = await fetchProductModels({
                        childId: productChildId,
                        typeId: system.product_type_id,
                        lineId: system.product_line_id,
                    });
                    setSelects((prev) => ({
                        ...prev,
                        product_models: models || [],
                    }));
                }
            } else {
                setValues({ toshiba_installation_system_id: value });
            }
        } catch (err: any) {
            customToast({
                title: err?.response?.data?.message || 'Failed to load system details.',
                variant: Variants.Danger,
            });
            setInstallationSystemSelected(false);
        } finally {
            setDisabled(false);
        }
    };

    return (
        <Stepper.Step loading={isLoading}>
            {/* System Details Section */}

            <SubTitle
                title={StringHelpers.title(
                    `${crud?.sub_titles?.system || 'System'} ${crud?.sub_titles?.details || 'Details'}`
                )}
            />

            {hasInstallation && (
                <FormInput
                    name='toshiba_installation_system_id'
                    type='searchable-select'
                    label={StringHelpers.title(
                        `${crud?.models?.installation || 'Installation'} ${crud?.sub_titles?.system || 'System'}`
                    )}
                    disabled={disabled}
                    options={selects.installation_systems}
                    value={store?.toshiba_installation_system_id}
                    onChange={(value) => {
                        handleInstallationSystemChange(value ?? null);
                    }}
                    errorMessages={errors?.toshiba_installation_system_id}
                    clearable={true}
                />
            )}

            <FormInput
                name='product_child_id'
                type='searchable-select'
                label={StringHelpers.title(crud?.sub_titles?.system_type || 'System Type')}
                disabled={disabled || (hasInstallation && !!installationProductChildId)}
                options={selects.product_children}
                value={store?.product_child_id}
                onChange={(value) => {
                    if (typeof value !== 'string') return;
                    setInstallationSystemSelected(false);
                    setValues({ product_child_id: value });
                }}
                errorMessages={errors?.product_child_id}
                clearable={false}
            />

            <FormInput
                name='product_type_id'
                type='searchable-select'
                label={StringHelpers.title(crud?.sub_titles?.location || 'Location')}
                disabled={disabled || installationSystemSelected}
                options={selects.product_types}
                value={store?.product_type_id}
                onChange={(value) => {
                    if (typeof value !== 'string') return;
                    setInstallationSystemSelected(false);
                    setValues({ product_type_id: value });
                }}
                errorMessages={errors?.product_type_id}
                clearable={false}
            />

            <FormInput
                name='product_line_id'
                type='searchable-select'
                label={StringHelpers.title(crud?.models?.product_line || 'Product Line')}
                disabled={disabled || installationSystemSelected}
                options={selects.product_lines}
                value={store?.product_line_id}
                onChange={(value) => {
                    if (typeof value !== 'string') return;
                    setInstallationSystemSelected(false);
                    setValues({ product_line_id: value });
                }}
                errorMessages={errors?.product_line_id}
                clearable={false}
            />

            <FormInput
                name='product_id'
                type='searchable-select'
                label={StringHelpers.title(crud?.sub_titles?.model_number || 'Model Number')}
                disabled={disabled || installationSystemSelected}
                options={selects.product_models}
                value={store?.product_id}
                onChange={(value) => {
                    if (typeof value !== 'string') return;
                    setValues({ product_id: value });
                }}
                errorMessages={errors?.product_id}
                clearable={false}
            />

            <FormInput
                name='serial_number'
                type='text'
                label={StringHelpers.title(fields?.serial_number || 'Serial Number')}
                disabled={disabled || installationSystemSelected}
                value={store?.serial_number}
                onChange={(value) => {
                    if (typeof value !== 'string') return;
                    setValues({ serial_number: value });
                }}
                errorMessages={errors?.serial_number}
            />

            {/* Other Section */}

            <SubTitle
                title={StringHelpers.title(crud?.sub_titles?.other || 'Other')}
            />

            <FormInput
                name='dispatch_note_number'
                type='text'
                label={StringHelpers.title(
                    fields?.dispatch_note_number
                    || 'Despatch Note Number'
                )}
                disabled={disabled}
                value={store?.dispatch_note_number}
                onChange={(value) => {
                    if (typeof value !== 'string') return;
                    setValues({ dispatch_note_number: value });
                }}
                errorMessages={errors?.dispatch_note_number}
            />

            <FormInput
                name='date_of_failure'
                type='date'
                label={StringHelpers.title(fields?.date_of_failure || 'Date of Failure')}
                disabled={disabled}
                value={store?.date_of_failure}
                onChange={(value) => {
                    if (typeof value !== 'string') return;
                    setValues({ date_of_failure: value });
                }}
                errorMessages={errors?.date_of_failure}
            />

            {isPostRepair && (
                <FormInput
                    name='date_of_repair'
                    type='date'
                    label={StringHelpers.title(fields?.date_of_repair || 'Date of Repair')}
                    disabled={disabled}
                    value={store?.date_of_repair}
                    onChange={(value) => {
                        if (typeof value !== 'string') return;
                        setValues({ date_of_repair: value });
                    }}
                    errorMessages={errors?.date_of_repair}
                />
            )}

            {isPreRepair && (
                <FormInput
                    name='proposed_date_of_repair'
                    type='date'
                    label={StringHelpers.title(
                        fields?.proposed_date_of_repair || 'Proposed Date of Repair'
                    )}
                    disabled={disabled}
                    value={store?.proposed_date_of_repair}
                    onChange={(value) => {
                        if (typeof value !== 'string') return;
                        setValues({ proposed_date_of_repair: value });
                    }}
                    errorMessages={errors?.proposed_date_of_repair}
                />
            )}

            <FormInput
                name='quantity_of_parts'
                type='number'
                label={StringHelpers.title(fields?.quantity_of_parts || 'Quantity of Parts')}
                disabled={disabled}
                value={store?.quantity_of_parts}
                inputMode='numeric'
                onChange={(value) => {
                    if (typeof value !== 'string') return;
                    setValues({ quantity_of_parts: value });
                }}
                errorMessages={errors?.quantity_of_parts}
            />
        </Stepper.Step>
    );
}

export default ClaimsStep2;
